import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { combineLatest, filter, Observable, of, map, switchMap } from 'rxjs';

import { ButtonPosition, ButtonSize, ButtonTheme, IconPosition, NotificationTheme } from '@fcom/ui-components';
import { AppState, ConfigService } from '@fcom/core';
import { Amount, LocationInfoMap, ServiceCatalogSelectionsForAllTravelers } from '@fcom/dapi';
import { isFinnairNorraOrWetLease, mapValues, unique } from '@fcom/core/utils';
import {
  Category,
  FinnairBoundItem,
  FinnairItineraryItemFlight,
  FinnairPassengerItem,
  FinnairServiceCatalogEligibilityReason,
  SubCategory,
} from '@fcom/dapi/api/models';
import { finShare } from '@fcom/rx';
import { Breakpoint } from '@fcom/common/interfaces/breakpoint.interface';

import { EligibilitiesForFragments, PurchasedServicesForOrder, UpdatedServicesForPassenger } from '../../interfaces';
import { getSeatType, isBoundBasedCategory, serviceMapping, ServiceMapping } from '../../utils';
import { seatSelections, serviceSelectionsForCategoryAllTravelers } from '../../../../store';
import {
  getTails,
  isBound,
  isBoundOrFlightConfirmedOrCheckedIn,
  isByBusOnly,
  isByTrainOnly,
  isFlight,
} from '../../../../utils/common-booking.utils';
import { PurchasesDonePerFragment } from '../product-list/product-list.component';

const translationKeys = {
  [Category.MEAL]: {
    title: 'services.meal.title',
    noSelection: 'services.meal.state.selection.default',
    notAvailable: 'ancillaries.meals.noMealsAvailable',
    maxedOut: 'ancillaries.meals.maxedOut',
    paid: 'ancillaries.meals.paid',
    inCart: 'ancillaries.meals.inCart',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_FRESH_MEAL_NOT_ELIGIBLE_EX_EXCEPT_HEL_WITHIN_24_HOURS]:
      'ancillaries.meals.eligibility.mealsNotAvailable',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_FRESH_MEAL_NOT_ELIGIBLE_EX_HEL_WITHIN_7_HOURS]:
      'ancillaries.meals.eligibility.mealsNotAvailable',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_PRE_ORDER_HOT_MEAL_NOT_ELIGIBLE_EX_HEL_WITHIN_16_HOURS]:
      'ancillaries.meals.eligibility.mealsNotAvailable',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_PRE_ORDER_HOT_MEAL_NOT_ELIGIBLE_EX_EXCEPT_HEL_36_HOURS]:
      'ancillaries.meals.eligibility.mealsNotAvailable',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_MEAL_NOT_ELIGIBLE_WHEN_ALREADY_FLOWN]:
      'ancillaries.meals.eligibility.boundFlown',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_SPECIAL_MEAL_NOT_ELIGIBLE]:
      'ancillaries.meals.eligibility.mealsNotAvailable',
  },
  [Category.WIFI]: {
    title: 'services.wifi.title',
    noSelection: 'services.wifi.state.selection.default',
    notAvailable: 'ancillaries.wifi.noWifiAvailable',
    maxedOut: 'ancillaries.wifi.maxedOut',
    paid: 'ancillaries.wifi.paid',
    inCart: 'ancillaries.wifi.inCart',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_WIFI_NOT_ELIGIBLE_WHEN_ALREADY_FLOWN]:
      'ancillaries.wifi.eligibility.boundFlown',
  },
  [Category.BAGGAGE]: {
    title: 'services.baggage.title',
    noSelection: 'services.baggage.state.selection.default',
    notAvailable: 'ancillaries.bags.noBagsAvailable',
    paid: 'ancillaries.bags.paid',
    inCart: 'ancillaries.bags.inCart',
    maxedOut: 'ancillaries.bags.maxedOut',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_BAGGAGE_NOT_ELIGIBLE_WHEN_ALREADY_FLOWN]:
      'ancillaries.bags.eligibility.boundFlown',
  },
  [Category.CABIN_BAGGAGE]: {
    title: 'services.cabinBaggage.title',
    noSelection: 'services.cabinBaggage.state.selection.default',
    notAvailable: 'ancillaries.cabinBaggage.notAvailable',
    paid: 'ancillaries.cabinBaggage.paid',
    inCart: 'ancillaries.cabinBaggage.inCart',
    maxedOut: 'ancillaries.cabinBaggage.maxedOut',
  },
  [Category.LOUNGE]: {
    title: 'services.lounge.title',
    noSelection: 'services.lounge.state.selection.default',
    notAvailable: 'ancillaries.lounge.noLoungeAvailable',
    maxedOut: 'ancillaries.lounge.maxedOut',
    paid: 'ancillaries.lounge.paid',
    inCart: 'ancillaries.lounge.inCart',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_LOUNGE_ACCESS_NOT_ELIGIBLE_WHEN_ALREADY_FLOWN]:
      'ancillaries.lounge.eligibility.boundFlown',
  },
  [Category.SPORT]: {
    title: 'services.sport.title',
    noSelection: 'services.sport.state.selection.default',
    notAvailable: 'ancillaries.sport.noSportAvailable',
    maxedOut: 'ancillaries.sport.maxedOut',
    paid: 'ancillaries.sport.paid',
    inCart: 'ancillaries.sport.inCart',
  },
  [Category.PET]: {
    title: 'services.pet.title',
    noSelection: 'services.pet.state.selection.default',
    notAvailable: 'ancillaries.pet.noPetAvailable',
    maxedOut: 'ancillaries.pet.maxedOut',
    paid: 'ancillaries.pet.paid',
    inCart: 'ancillaries.pet.inCart',
    middleNotificationTranslationKey: 'services.pet.sideBarMiddleNotification',
  },
  [Category.SEAT]: {
    title: 'services.seat.title',
    noSelection: 'services.seat.state.selection.default',
    notAvailable: 'ancillaries.seats.noSeatsAvailable',
    paid: 'ancillaries.seats.paid',
    inCart: 'ancillaries.seats.inCart',
    maxedOut: 'ancillaries.seats.maxedOut',
    [FinnairServiceCatalogEligibilityReason.PURCHASE_SEAT_NOT_ELIGIBLE_WHEN_ALREADY_FLOWN]:
      'ancillaries.seat.eligibility.boundFlown',
  },
  [Category.PRIORITY]: {
    title: 'services.priority.title',
    noSelection: 'services.priority.state.selection.default',
    notAvailable: 'ancillaries.priority.noPriorityAvailable',
    maxedOut: 'ancillaries.priority.maxedOut',
    paid: 'ancillaries.priority.paid',
    inCart: 'ancillaries.priority.inCart',
  },
};

const translationForBenefits = {
  [SubCategory.CHECKED_BAGGAGE]: {
    title: 'benefits.checkedBaggage.title',
    description: 'benefits.checkedBaggage.description',
  },
};

@Component({
  selector: 'fin-ancillary-sidebar',
  styleUrls: ['ancillary-sidebar.component.scss'],
  templateUrl: 'ancillary-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AncillarySidebarComponent implements OnInit {
  readonly Breakpoint = Breakpoint;
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly Category = Category;
  readonly SubCategory = SubCategory;
  readonly IconPosition = IconPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  category$: Observable<Category>;

  @Input()
  isSideBarOpen = false;

  @Input()
  boundsOrFlights$: Observable<Array<FinnairBoundItem | FinnairItineraryItemFlight>> = of([]);

  @Input()
  availability$: Observable<EligibilitiesForFragments>;

  @Input()
  maxedOutForFragment$: Observable<PurchasesDonePerFragment>;

  @Input()
  subTotalPrice$: Observable<Amount>;

  @Input()
  locations: LocationInfoMap;

  @Input()
  passengers: FinnairPassengerItem[] = [];

  @Input()
  subviewName: string;

  @Input()
  showFareFamily = true;

  @Input()
  purchasedServices$: Observable<PurchasedServicesForOrder> = of({});

  @Input()
  skipEnabled = false;

  @Input()
  skipText = '';

  @Input()
  passengersWithPetcIncompatibleSeats$: Observable<Record<string, string[]>> = of({});

  @Input()
  ctaButtonText = 'ancillaries.backToServices';

  @Output()
  ctaSideBarClosed: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  sideBarClosed: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  changeSelectedIndex: EventEmitter<number> = new EventEmitter<number>();

  services$: Observable<ServiceCatalogSelectionsForAllTravelers>;
  service$: Observable<ServiceMapping>;
  updatedServices$: Observable<UpdatedServicesForPassenger>;
  includedBenefits$: Observable<ServiceCatalogSelectionsForAllTravelers>;
  translations$: Observable<Record<string, string> | undefined>;
  benefitTranslations$: Observable<Record<string, string> | null>;
  noAncillaryBecauseAirlineForFragment$: Observable<Record<string, string>>;
  noAncillaryBecauseAirlineKey$: Observable<string>;
  confirmedOrCheckedInSegments$: Observable<Record<string, boolean>>;
  tails$: Observable<Record<string, string[]>>;
  isBusOnlyConnections$: Observable<Record<string, boolean>>;
  isTrainOnlyConnections$: Observable<Record<string, boolean>>;
  priorityBasedEligibilities$: Observable<Record<string, string[]>>;
  displayAsBoundBased$: Observable<boolean>;
  middleNotificationTranslationKey$: Observable<string>;

  constructor(
    private store$: Store<AppState>,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.displayAsBoundBased$ = this.category$.pipe(map(isBoundBasedCategory));

    this.noAncillaryBecauseAirlineKey$ = this.category$.pipe(
      map((category) =>
        isBoundBasedCategory(category)
          ? 'ancillaries.bags.noBagsAvailableAirlines'
          : 'ancillaries.seats.noSeatsAvailableAirline'
      )
    );

    this.noAncillaryBecauseAirlineForFragment$ = this.boundsOrFlights$.pipe(
      map((boundsOrFlights) => {
        return boundsOrFlights.reduce((acc, cur) => {
          if (isBound(cur)) {
            acc[cur.id] = cur.itinerary
              .filter(isFlight)
              .map((i: FinnairItineraryItemFlight) => i.operatingAirline)
              .filter((operatingAirline) => !isFinnairNorraOrWetLease(operatingAirline.name, operatingAirline.code))
              .map((operatingAirline) => operatingAirline.name)
              .filter(unique)
              .join(', ');
          } else {
            acc[cur.id] = !isFinnairNorraOrWetLease(cur.operatingAirline.name, cur.operatingAirline.code)
              ? cur.operatingAirline.name
              : undefined;
          }
          return acc;
        }, {});
      }),
      finShare()
    );
    this.service$ = this.category$.pipe(map((category) => serviceMapping.find((s) => s.category === category)));
    this.translations$ = this.category$.pipe(map((category) => translationKeys[category]));

    this.includedBenefits$ = this.category$.pipe(
      switchMap((category) =>
        category === Category.BAGGAGE
          ? this.store$.pipe(
              serviceSelectionsForCategoryAllTravelers(category),
              map((selections) =>
                mapValues(selections, (selection) =>
                  selection.filter((s) => s.category === SubCategory.CHECKED_BAGGAGE)
                )
              ),
              finShare()
            )
          : of({})
      ),
      finShare()
    );

    this.benefitTranslations$ = this.category$.pipe(
      map((category) => (category === Category.BAGGAGE ? translationForBenefits[SubCategory.CHECKED_BAGGAGE] : null))
    );

    this.services$ = this.category$.pipe(
      switchMap((category) => {
        switch (category) {
          case Category.SEAT:
            return this.store$.pipe(seatSelections(this.configService.cfg.enableBookingFlowAncillaryBundles));
          case Category.WIFI:
            return this.store$.pipe(
              serviceSelectionsForCategoryAllTravelers(
                category,
                this.configService.cfg.enableBookingFlowAncillaryBundles
              ),
              finShare()
            );
          case Category.BAGGAGE:
            return this.store$.pipe(
              serviceSelectionsForCategoryAllTravelers(category),
              map((selections) =>
                mapValues(selections, (selection) =>
                  selection.filter((s) => s.category !== SubCategory.CHECKED_BAGGAGE)
                )
              ),
              finShare()
            );
          default:
            return this.store$.pipe(serviceSelectionsForCategoryAllTravelers(category), finShare());
        }
      }),
      finShare()
    );

    this.confirmedOrCheckedInSegments$ = this.boundsOrFlights$.pipe(
      map((boundsOrFlights) => {
        return boundsOrFlights.reduce((all, boundOrFlight) => {
          all[boundOrFlight.id] = isBoundOrFlightConfirmedOrCheckedIn(boundOrFlight);
          return all;
        }, {});
      })
    );

    this.tails$ = this.boundsOrFlights$.pipe(
      map((boundsOrFlights) => {
        return boundsOrFlights.reduce((all, boundOrFlight) => {
          all[boundOrFlight.id] = getTails(boundOrFlight);
          return all;
        }, {});
      })
    );

    this.isBusOnlyConnections$ = this.boundsOrFlights$.pipe(
      map((boundsOrFlights) => {
        return boundsOrFlights.reduce((all, boundOrFlight) => {
          all[boundOrFlight.id] = isByBusOnly(boundOrFlight);
          return all;
        }, {});
      })
    );

    this.isTrainOnlyConnections$ = this.boundsOrFlights$.pipe(
      map((boundsOrFlights) => {
        return boundsOrFlights.reduce((all, boundOrFlight) => {
          all[boundOrFlight.id] = isByTrainOnly(boundOrFlight);
          return all;
        }, {});
      })
    );

    this.priorityBasedEligibilities$ = combineLatest([
      this.boundsOrFlights$.pipe(map((bounds) => bounds.map(({ id }) => id))),
      this.availability$,
      this.isBusOnlyConnections$,
      this.noAncillaryBecauseAirlineForFragment$,
      this.translations$,
    ]).pipe(
      map(([fragmentIds, availability, busOnly, otherAirline, translations]) => {
        return fragmentIds.reduce((all, fragmentId) => {
          if (busOnly[fragmentId]) {
            all[fragmentId] = [translations?.['notAvailable'] + 'Bus'];
          } else if (otherAirline[fragmentId]) {
            all[fragmentId] = [translations?.['notAvailable']];
          } else if (availability[fragmentId] && availability[fragmentId].every((e) => !e.isAllowedToUse)) {
            const eligibilities = availability[fragmentId]
              .map((e) => translations?.[e.reason])
              .filter(Boolean)
              .filter(unique);
            all[fragmentId] = eligibilities.length > 0 ? eligibilities : ['services.cannotAdd'];
          }
          return all;
        }, {});
      }),
      finShare()
    );

    const includedServices$ = combineLatest([this.purchasedServices$, this.services$]).pipe(
      map(([purchasedServices, services]) => {
        return Object.keys({ ...purchasedServices }).reduce(
          (filteredPurchaseServices, boundOrFlightId) => {
            filteredPurchaseServices[boundOrFlightId] = purchasedServices[boundOrFlightId].filter(
              (purchasedService) => {
                const serviceForBoundOrFlight = services[boundOrFlightId];
                if (
                  ![SubCategory.SEAT, SubCategory.MEAL, SubCategory.SPECIAL_DIET].includes(purchasedService.category)
                ) {
                  return true;
                }
                return (
                  serviceForBoundOrFlight?.some(
                    (selectedService) =>
                      selectedService.travelerId === purchasedService.travelerId &&
                      ((selectedService.category === SubCategory.SEAT &&
                        selectedService.variant === purchasedService.seatNumber) ||
                        selectedService.variant === purchasedService.variant)
                  ) ?? true
                );
              }
            );
            return filteredPurchaseServices;
          },
          { ...purchasedServices }
        );
      })
    );

    this.updatedServices$ = combineLatest([this.services$, includedServices$]).pipe(
      map(([services, included]) => {
        const passengerNames = this.passengers.reduce((acc, { id, firstName, lastName }) => {
          acc[id] = `${firstName} ${lastName}`;
          return acc;
        }, {});
        const updatedServices = {};
        Object.entries(services).forEach(([fragmentId, servicesFromStore]) => {
          servicesFromStore
            .filter((service) => service.quantity > 0 && !service.paid)
            .filter((service) => {
              return !Object.entries(included).some(([id, includedServices]) => {
                return (
                  fragmentId === id &&
                  includedServices.some(
                    (s) =>
                      s.travelerId === service.travelerId &&
                      s.variant === service.variant &&
                      s.quantity === service.quantity
                  )
                );
              });
            })
            .map((s) =>
              s.category === SubCategory.SEAT_WIFI
                ? {
                    ...s,
                    seatTypeLabel: this.getSeatTypeFromServiceVariant(s.variant).label,
                    includedProductVariant: s.variant.split('_')[2],
                  }
                : s
            )
            .forEach((service) => {
              updatedServices[fragmentId] = updatedServices[fragmentId] || {};
              if (updatedServices[fragmentId][service.travelerId]) {
                updatedServices[fragmentId][service.travelerId].added.push(service);
              } else {
                updatedServices[fragmentId][service.travelerId] = {
                  name: passengerNames[service.travelerId],
                  added: [service],
                };
              }
            });
        });

        Object.entries(included).forEach(([fragmentId, includedServices]) => {
          includedServices
            .filter((service) => service.quantity > 0)
            .forEach((service) => {
              updatedServices[fragmentId] = updatedServices[fragmentId] || {};
              if (updatedServices[fragmentId]?.[service.travelerId]) {
                if (updatedServices[fragmentId][service.travelerId].included) {
                  updatedServices[fragmentId][service.travelerId].included.push(service);
                } else {
                  updatedServices[fragmentId][service.travelerId].included = [service];
                }
              } else {
                updatedServices[fragmentId] = {
                  ...updatedServices[fragmentId],
                  [service.travelerId]: { name: passengerNames[service.travelerId], included: [service] },
                };
              }
            });
        });
        return updatedServices;
      })
    );

    this.middleNotificationTranslationKey$ = combineLatest([
      this.category$,
      this.services$,
      this.passengersWithPetcIncompatibleSeats$,
    ]).pipe(
      filter(([category]) => category === Category.PET),
      map(([_, services, passengersWithPetcIncompatibleSeats]) => {
        return Object.keys(passengersWithPetcIncompatibleSeats)?.some((boundId) => {
          const travelerIds = passengersWithPetcIncompatibleSeats[boundId];
          return (
            travelerIds &&
            services &&
            services[boundId]?.some(
              (service) => service.category === SubCategory.PET_IN_CABIN && travelerIds.includes(service.travelerId)
            )
          );
        })
          ? translationKeys[Category.PET].middleNotificationTranslationKey
          : undefined;
      })
    );
  }

  toType(t: FinnairBoundItem | FinnairItineraryItemFlight): FinnairBoundItem | FinnairItineraryItemFlight {
    return 'fareFamily' in t ? (t as FinnairBoundItem) : (t as FinnairItineraryItemFlight);
  }

  getSeatTypeFromServiceVariant(variant: string): { className: string; label: string; cmsKey: string } {
    const serviceCategories = variant?.split('_');
    return getSeatType(serviceCategories);
  }
}
